import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "./AuthContext";
import { SessionUser } from "../types/interfaces";
import { ALERT_INITIAL_STATE } from "../utils/data";
import { apiRequest, ApiError } from "../hooks/apiRequest";

interface Props {
  children: JSX.Element | JSX.Element[];
}

export const IDLE_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const AuthProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [userData, setUserData] = useState<SessionUser | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSuccess, setIsSuccess] = useState(ALERT_INITIAL_STATE);
  const [isError, setIsError] = useState(ALERT_INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalRecoverOpen, setIsModalRecoverOpen] = useState(false);
  const [isModalChangePasswordVisible, setIsModalChangePasswordVisible] = useState(false);
  const [isModalResetPasswordVisible, setIsModalResetPasswordVisible] = useState(false);

  const isManager = userData?.role === "manager";

  const loadSession = async () => {
    try {
      const { data } = await apiRequest<SessionUser>("auth/me");
      setUserData(data);
      setIsLoggedIn(true);
    } catch {
      setUserData(null);
      setIsLoggedIn(false);
      localStorage.removeItem("info");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSuccess(ALERT_INITIAL_STATE);
      setIsError(ALERT_INITIAL_STATE);
    }, 3000);
    return () => clearTimeout(timer);
  }, [isSuccess, isError]);

  const resetCredentials = useCallback(() => {
    setUsername("");
    setPassword("");
  }, []);

  const isLoggingOutRef = useRef(false);

  const logoutHandler = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    setIsLoading(true);
    try {
      await apiRequest("auth/logout", { method: "POST" });
    } catch {
      // best-effort — the client-side state clears regardless
    }
    localStorage.removeItem("info");
    setUserData(null);
    setIsLoggedIn(false);
    resetCredentials();
    navigate("/");
    setIsLoading(false);
    isLoggingOutRef.current = false;
  }, [navigate, resetCredentials]);

  useEffect(() => {
    const handleForcedLogout = () => logoutHandler();
    window.addEventListener("auth:logout", handleForcedLogout);
    return () => window.removeEventListener("auth:logout", handleForcedLogout);
  }, [logoutHandler]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    let timer = window.setTimeout(logoutHandler, IDLE_TIMEOUT_MS);
    const resetIdleTimer = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(logoutHandler, IDLE_TIMEOUT_MS);
    };
    const activityEvents: (keyof WindowEventMap)[] = ["mousedown", "keydown", "touchstart", "scroll"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer));

    return () => {
      window.clearTimeout(timer);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));
    };
  }, [isLoggedIn, logoutHandler]);

  const loginHandler = async () => {
    setIsLoading(true);
    try {
      const { data } = await apiRequest<{ accessToken: string }>("auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      localStorage.setItem("info", data.accessToken);
      await loadSession();
      navigate("/projects");
    } catch (e) {
      resetCredentials();
      setIsError({ status: true, message: e instanceof ApiError ? e.message : "Not Authorized" });
    } finally {
      setIsLoading(false);
    }
  };

  const validateToken = (): boolean => !!localStorage.getItem("info");

  const handleChangePassword = ({
    data,
    next,
  }: {
    data: { currentPassword: string; newPassword: string };
    next: () => void;
  }) => {
    setIsLoading(true);
    apiRequest("auth/change-password", { method: "PUT", body: JSON.stringify(data) })
      .then(() => {
        setIsSuccess({ status: true, message: "Password changed" });
        next();
      })
      .catch((e) =>
        setIsError({ status: true, message: e instanceof ApiError ? e.message : "Please try again" })
      )
      .finally(() => setIsLoading(false));
  };

  const handleSendResetPassword = ({ username: target, next }: { username: string; next: () => void }) => {
    setIsLoading(true);
    apiRequest("auth/reset-password/request", {
      method: "POST",
      body: JSON.stringify({ usernameOrEmail: target }),
    })
      .then(() => {
        setIsSuccess({ status: true, message: "Password Sent" });
        next();
      })
      .catch((e) =>
        setIsError({ status: true, message: e instanceof ApiError ? e.message : "Please try Again" })
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <AuthContext.Provider
      value={{
        username,
        password,
        isLoggedIn,
        isSuccess,
        isError,
        isLoading,
        isManager,
        userData,
        isModalRecoverOpen,
        isModalChangePasswordVisible,
        isModalResetPasswordVisible,
        openCloseModalPassword: (value) => setIsModalChangePasswordVisible(value),
        onOpenCloseModalRecover: (value: boolean) => setIsModalRecoverOpen(value),
        openCloseModalResetPassword: (value) => setIsModalResetPasswordVisible(value),
        handleSendResetPassword,
        handleChangePassword,
        onChangeUsername: (value: string) => setUsername(value),
        onChangePassword: (value: string) => setPassword(value),
        handleLogin: loginHandler,
        onLogout: logoutHandler,
        validateToken,
        reportError: (message: string) => setIsError({ status: true, message }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
